import { DashWrapper } from "components/DashboardContainer/UnauthenticatedDash";
import { setPostLoginRedirect } from "utils/redirects";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router";
import {
  getLaunchableDeployOverrides,
  getLaunchableIdFromSearch,
  setLaunchableDeployOverrides,
} from "components/Launchables/DeployRedesign/shared/page/queryParams";
import { Spinner } from "@kui-react/spinner";

import posthog from "posthog-js";
import { useLaunchableDeployQuery } from "components/Launchables/DeployRedesign/shared/hooks/useLaunchableDeployQuery";
import { useKas } from "contexts/KasContext";
import { CONSOLE_1_KAS_DEPLOYMENT_URL } from "server/kas/utils";
import { DeployPageContent } from "components/Launchables/DeployRedesign/shared/page/DeployPageContent";
import { useInstanceTypeQuery } from "server/devplane/instanceTypes/queries";
import { useLaunchableLifecycleScriptQuery } from "server/brevdeploy/launchables/queries";
import { parseInstanceType } from "utils/instanceTypes";
import { useLaunchableComputeSelection } from "components/Launchables/DeployRedesign/shared/hooks/useLaunchableComputeSelection";
import { getPrimaryInstanceCloudCredId } from "utils/cloudCreds";

const LaunchableDeployPublic: React.FC = () => {
  const location = useLocation();

  const { isUserLoggedIn, isLoading: isAuthLoading } = useKas();

  const history = useHistory();
  const queryString = useMemo(
    () => new URLSearchParams(location.search).toString(),
    [location.search],
  );
  const deployOverrides = useMemo(
    () => getLaunchableDeployOverrides(location.search),
    [location.search],
  );
  const queryLaunchableID = getLaunchableIdFromSearch(location.search);
  const {
    launchableData,
    rawLaunchableData,
    loading,
    retrying,
    launchableNotFound,
    launchableAccessError,
    launchableLoadError,
    refetch,
  } = useLaunchableDeployQuery(queryLaunchableID || "", true);
  const viewedLaunchableId = useRef<string | null>(null);

  const lifecycleScriptId =
    launchableData?.containerSelected?.vmBuild?.lifeCycleScriptAttr?.id;
  const {
    data: lifecycleScript = null,
    isInitialLoading: lifecycleScriptLoading,
  } = useLaunchableLifecycleScriptQuery(
    queryLaunchableID || "",
    lifecycleScriptId || "",
  );
  const intendedInstanceType =
    deployOverrides.instanceType ?? launchableData?.instanceType ?? null;
  const intendedCloudCredentialId =
    deployOverrides.cloudCredentialId ?? launchableData?.cloudCredId ?? null;
  const intendedDiskStorage =
    deployOverrides.diskStorage ?? launchableData?.diskStorage ?? null;
  // Preserve explicit "?location=any" (null) vs unset (undefined) -- matches DeployPageContent.
  const intendedLocation =
    deployOverrides.location !== undefined
      ? deployOverrides.location
      : (launchableData?.location ?? null);
  const parsedInstanceType =
    parseInstanceType(intendedInstanceType)?.instanceType ?? "";
  const { data: intendedInstance, isInitialLoading: loadingInstance } =
    useInstanceTypeQuery(
      parsedInstanceType,
      intendedCloudCredentialId || "",
      intendedLocation,
    );
  const {
    instanceType: currentInstanceTypeObject,
    storage: currentStorage,
    setSelection: handleSetNewCurrentInstanceType,
  } = useLaunchableComputeSelection({
    launchableId: queryLaunchableID || "",
    intendedInstance,
    intendedStorage: intendedDiskStorage,
  });

  useEffect(() => {
    if (
      !launchableData ||
      !queryLaunchableID ||
      viewedLaunchableId.current === queryLaunchableID
    ) {
      return;
    }

    viewedLaunchableId.current = queryLaunchableID;
    posthog.capture("Launchable Viewed", {
      launchableID: queryLaunchableID,
      launchableInstanceType: launchableData.instanceType,
      cloudCredID: launchableData.cloudCredId,
      launchableCreatedByUserID: launchableData.userId,
      launchableCreatedByOrgID: launchableData.orgId,
      launchableRawURL: location.pathname + location.search,
    });
  }, [launchableData, location.pathname, location.search, queryLaunchableID]);

  const handleDeployNow = useCallback(
    (selectedLocation?: string | null) => {
      const deployQuery = setLaunchableDeployOverrides(queryString, {
        instanceType: currentInstanceTypeObject?.type,
        cloudCredentialId: getPrimaryInstanceCloudCredId(
          currentInstanceTypeObject,
        ),
        diskStorage: currentStorage ?? undefined,
        location: selectedLocation,
      });
      const deployPath = `/launchable/deploy/now?forceLogin=true&${deployQuery}`;
      setPostLoginRedirect(deployPath);
      window.open(`${CONSOLE_1_KAS_DEPLOYMENT_URL}${deployPath}`, "_blank");
    },
    [currentInstanceTypeObject, currentStorage, queryString],
  );

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (isUserLoggedIn) {
      history.push(`/launchable/deploy/now?${queryString}`);
    }
  }, [history, isAuthLoading, isUserLoggedIn, queryString]);
  const isPageLoading = loading || lifecycleScriptLoading || loadingInstance;

  if (isPageLoading) {
    return (
      <DashWrapper>
        <div className="h-screen flex justify-center items-center">
          <Spinner />
        </div>
      </DashWrapper>
    );
  }

  return (
    <DashWrapper>
      <DeployPageContent
        key={queryLaunchableID}
        launchableId={queryLaunchableID}
        intendedInstanceType={intendedInstanceType}
        intendedCloudCredId={intendedCloudCredentialId}
        intendedDiskStorage={intendedDiskStorage}
        defaultInstanceType={intendedInstance ?? null}
        onSetNewCurrentInstanceType={handleSetNewCurrentInstanceType}
        loading={loading || lifecycleScriptLoading}
        launchableNotFound={launchableNotFound}
        launchableAccessError={launchableAccessError}
        launchableLoadError={launchableLoadError}
        retryingLaunchable={retrying}
        onRetryLaunchable={() => {
          void refetch();
        }}
        launchableData={launchableData}
        rawLaunchableData={rawLaunchableData}
        instanceLoading={loadingInstance}
        instanceTypeObject={currentInstanceTypeObject}
        instanceStorage={currentStorage || intendedDiskStorage || ""}
        nameErrorMessage={""}
        allInstancesAvailable={[]}
        onDeploy={(_ngcApiKeySpec, _parameterBindings, selectedLocation) => {
          handleDeployNow(selectedLocation);
        }}
        lifecycleScript={lifecycleScript}
        isPublic={true}
        initialLocation={deployOverrides.location}
      />
    </DashWrapper>
  );
};

export default LaunchableDeployPublic;
